import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';
import { AuthShell } from '@/components/auth/auth-shell';
import { SocialButtons } from '@/components/auth/social-buttons';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';
import {
  type AuthPageSearch,
  authorizeReplayUrl,
  consentPageUrl,
  hardNavigate,
  socialErrorMessage,
  withCurrentSearch,
} from '@/lib/oauth';

export function LoginPage() {
  const navigate = useNavigate();
  // Grant params arrive in two shapes: the authorize endpoint's bounce
  // (client_id + redirect_uri + full original query) and the consent screen's
  // bounce for logged-out visitors (code + client_id, code IS the consent_code).
  // A third param, `error`, appears when a social sign-in is cancelled/fails
  // at the provider and better-auth returns to this page (socialErrorUrl).
  const search = useSearch({ strict: false }) as AuthPageSearch;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(() => socialErrorMessage(search.error));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error: signInError } = await authClient.signIn.email({ email, password });
      if (signInError) {
        setError(signInError.message ?? 'Sign in failed. Check your email and password.');
      } else if (search.client_id && search.redirect_uri) {
        // Authorize bounce — replay the original authorize request verbatim;
        // the fresh session lets it proceed to the consent screen.
        hardNavigate(authorizeReplayUrl());
      } else if (search.code && search.client_id) {
        // Consent-screen bounce — back to consent with the fresh session.
        hardNavigate(consentPageUrl(search.code, search.client_id));
      } else if (search.invitation) {
        // Invitation detour — back to the accept page, which now has a session
        // to accept with. Without this the invitee lands on their own workspace
        // and the invitation is left pending.
        await navigate({
          to: '/accept-invitation/$invitationId',
          params: { invitationId: search.invitation },
        });
      } else {
        // `/` picks the destination: dashboard for a workspace that's already
        // connected something, the wizard only for one that hasn't.
        await navigate({ to: '/' });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AuthShell>
        <div className="mb-6">
          <h1 className="text-lg font-semibold">Welcome back</h1>
          <p className="mt-1 text-sm text-faint">Sign in to your workspace.</p>
        </div>
        <SocialButtons search={search} onError={setError} />
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-4"
        >
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error) setError(null);
              }}
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="password">Password</Label>
              {/* Recovery drops any in-flight grant (plan password-reset D3):
                  the email round-trip may switch devices, so no search carried. */}
              <Link to="/forgot-password" className="text-xs text-faint hover:underline">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(null);
              }}
              placeholder="••••••••"
            />
          </div>
          <Button type="submit" size="cta" className="w-full" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-faint">
          New to Brain?{' '}
          {/* Plain anchor: carries an in-flight OAuth grant query verbatim
              without router search-param typing at every Link call site. */}
          <a
            href={withCurrentSearch('/signup')}
            className="font-semibold text-foreground hover:underline"
          >
            Create an account
          </a>
        </p>
      </AuthShell>
    </div>
  );
}
